import { Button, Form, Select, Input } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getVehForLocation } from '../../offices/offices-selectors';
import { capitalize } from 'lodash';
import { createLocation, unassignStuff, updateContact } from '../../offices/offices-actions';
import { vehConfig } from '../configs/veh-config';
import { getAdminEmails, getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { formItemMap } from '@med-fe/ui';
import moment from 'moment';
import { getContacts } from '../../offices/offices-selectors';
import { fetchContacts } from '../../offices/offices-actions';
import { useMsal } from '@azure/msal-react';
import { fetchAdminEmails } from 'apps/mod/src/store/user/user-actions';

function VehDetails({ locId }) {
  const [form] = Form.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useDispatch();
  const data = useSelector((state) => getVehForLocation(state, locId));
  const isEditAllowed = useSelector(getWriteTabsPermissions).veh;
  const doctors = useSelector(getContacts);
  const [isCombination, setIsCombination] = useState<boolean>(false);
  const [vehDoctor, setVehDoctor] = useState<any>({});
  const [doctorSearch, setDoctorSearch] = useState('');
  const { accounts } = useMsal();
  const adminEmails = useSelector(getAdminEmails);
  const [allowDateClear, setAllowDateClear] = useState<boolean>(false);
  const hasMounted = useRef(false);
  const [vehGoLive, setVehGoLive] = useState(data?.vehGoLive ? data?.vehGoLive : null);
  const [vehLastDay, setVehLastDay] = useState(data?.vehLastDay ? data?.vehLastDay : null);
  const [callCenter, setCallCenter] = useState(data?.callCenter ? data?.callCenter : null);
  const [pxpLaunch, setPxpLaunch] = useState(data?.pxpLaunch ? data?.pxpLaunch : null);
  const [isVehGoLiveConfirmVisible, setVehGoLiveConfirmVisible] = useState(false);
  const [isVehLastDayConfirmVisible, setVehLastDayConfirmVisible] = useState(false);
  const [isCallCenterConfirmVisible, setCallCenterConfirmVisible] = useState(false);
  const [isPxpLaunchConfirmVisible, setPxpLaunchConfirmVisible] = useState(false);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchAdminEmails());
  }, []);

  useEffect(() => {
    setAllowDateClear(adminEmails?.some((user) => user?.trim().toLowerCase() === accounts[0]?.username));
  }, [adminEmails, accounts]);

  useEffect(() => {
    setVehDoctor(data.doctor);
    setIsCombination(data.doctorNetwork === 'Combination');
    if (data.doctorNetwork === 'Combination') {
      dispatch(fetchContacts({ job: 'OD', name: '' }));
    }
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      ...(!!data.callCenter && { callCenter: moment(data.callCenter) }),
      ...(!!data.vehLastDay && { vehLastDay: moment(data.vehLastDay) }),
      ...(!!data.vehGoLive && { vehGoLive: moment(data.vehGoLive) }),
      ...(!!data.pxpLaunch && { pxpLaunch: moment(data.pxpLaunch) }),
    });
  }, [locId]);

  useEffect(() => {
    if (vehDoctor && data.doctor) {
      setIsDisabled(vehDoctor?.id === data.doctor?.id);
    }
  }, [vehDoctor, data.doctor]);

  const onSubmit = (formData: any) => {
    const { teleOptometry, ...formDataValues } = formData;
    const payload = {
      locationVeh: {
        ...formDataValues,
        ...(!!formDataValues.callCenter && { callCenter: callCenter }),
        ...(!!formDataValues.vehLastDay && { vehLastDay: vehLastDay }),
        ...(!!formDataValues.vehGoLive && { vehGoLive: vehGoLive }),
        ...(!!formDataValues.pxpLaunch && { pxpLaunch: pxpLaunch }),
        id: data.vehId,
      },
      id: locId,
      teleOptometry: teleOptometry,
    };
    dispatch(createLocation(payload));

    if (data.doctor && (vehDoctor.id !== data.doctor.id || (!isCombination && data.doctor.id))) {
      dispatch(unassignStuff({ locationId: locId, contactId: data.doctor.id }));
    }
    if (vehDoctor.id && ((data.doctor && vehDoctor?.id !== data.doctor.id) || (isCombination && vehDoctor.id))) {
      dispatch(
        updateContact({
          assign: { contactId: vehDoctor.id, locationId: locId, job: 'TELE', privateLocation: false },
        })
      );
    }
  };

  const onDoctorNetworkChange = (value) => {
    if (value === 'Open Network') {
      setIsCombination(false);
      setVehDoctor({});
      setDoctorSearch('');
    } else if (value === 'Combination') {
      setIsCombination(true);
      dispatch(fetchContacts({ job: 'OD', name: '' }));
    }
  };

  const onVehGoLiveSelect = (value) => {
    form.setFieldsValue({ vehGoLive: value ? value.format('YYYY-MM-DD') : null });
    setVehGoLive(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onVehLastDaySelect = (value) => {
    form.setFieldsValue({ vehLastDay: value ? value.format('YYYY-MM-DD') : null });
    setVehLastDay(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onCallCenterSelect = (value) => {
    form.setFieldsValue({ callCenter: value ? value.format('YYYY-MM-DD') : null });
    setCallCenter(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onpxpLaunchSelect = (value) => {
    form.setFieldsValue({ pxpLaunch: value ? value.format('YYYY-MM-DD') : null });
    setPxpLaunch(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onConfirmSetVisible = (value, name) => {
    if (name == 'VEH Go Live') setVehGoLiveConfirmVisible(value);
    if (name == 'VEH Last Day') setVehLastDayConfirmVisible(value);
    if (name == 'Call Center') setCallCenterConfirmVisible(value);
    if (name == 'PXP Launch') setPxpLaunchConfirmVisible(value);
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className='pro-service-details-form'
      onFinish={(formData) => {
        onSubmit(formData);
        setIsDisabled(true);
      }}
      onValuesChange={() => setIsDisabled(false)}
    >
      {Object.entries(vehConfig).map(([key, value]: any) => {
        const params = {
          key,
          label: capitalize(value.label),
          name: key,
          rules: value.rules,
          data: value.data,
          ...(value.type === 'checkbox' && { valuePropName: 'checked' }),
        };
        const formItemParams = {
          ...value,
          allowDateClear: allowDateClear,
          ...(key === 'vehGoLive' && {
            onSelect: onVehGoLiveSelect,
            data: vehGoLive,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isVehGoLiveConfirmVisible,
            name: value.label,
          }),
          ...(key === 'vehLastDay' && {
            onSelect: onVehLastDaySelect,
            data: vehLastDay,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isVehLastDayConfirmVisible,
            name: value.label,
          }),
          ...(key === 'callCenter' && {
            onSelect: onCallCenterSelect,
            data: callCenter,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isCallCenterConfirmVisible,
            name: value.label,
          }),
          ...(key === 'pxpLaunch' && {
            onSelect: onpxpLaunchSelect,
            data: pxpLaunch,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isPxpLaunchConfirmVisible,
            name: value.label,
          }),
        };
        return <Form.Item {...params}>{formItemMap(formItemParams, !isEditAllowed)}</Form.Item>;
      })}

      <Form.Item label='Doctor Network' key={'doctorNetwork'} name='doctorNetwork'>
        <Select
          allowClear
          disabled={!isEditAllowed}
          onClear={() => {
            setVehDoctor({});
            dispatch(fetchContacts({ job: 'OD', name: '' }));
            setIsCombination(false);
            setDoctorSearch('');
          }}
          onSelect={(value) => onDoctorNetworkChange(value)}
        >
          <Select.Option key={'Open Network'}>Open Network</Select.Option>
          <Select.Option key={'Combination'}>Combination</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        tooltip={!isCombination && 'only for doctor network is combination'}
        label='Doctor (search by Name or create new)'
        className='form-item-with-custom-tooltip'
      >
        <Select
          allowClear
          showSearch
          disabled={!isCombination || !isEditAllowed}
          value={doctorSearch}
          onChange={setDoctorSearch}
          onClear={() => setVehDoctor({})}
          onSearch={(value) => {
            dispatch(fetchContacts({ job: 'OD', name: value }));
          }}
          onSelect={(event, value) => {
            setVehDoctor(value);
          }}
          options={doctors.map(({ name, ...info }) => ({ ...info, name, value: name }))}
          defaultValue={vehDoctor?.name}
        />
      </Form.Item>
      {vehDoctor && isCombination && (
        <div>
          <Form.Item label={'Doctor Name'}>
            <Input disabled value={vehDoctor?.name} />
          </Form.Item>
          <Form.Item label={'Doctor Phone'}>
            <Input disabled value={vehDoctor?.phone} />
          </Form.Item>
          <Form.Item label={'Doctor Email'}>
            <Input disabled value={vehDoctor?.email} />
          </Form.Item>
          <Form.Item label={'Doctor Start Date'}>
            <Input disabled value={vehDoctor?.startDate} />
          </Form.Item>
        </div>
      )}
      <Form.Item>
        <Button type='primary' htmlType='submit' className={'btn-form-action'} disabled={!isEditAllowed || isDisabled}>
          SAVE
        </Button>
      </Form.Item>
    </Form>
  );
}

export default VehDetails;
